import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import {
  PostScheduleController,
  PostScheduleControllerProps,
  PostScheduleViewContext
} from './PostSchedule.controller';
import { gql } from '@apollo/client';
import {
  QueueContext,
  QueueInput,
  QueueSortOrder,
  QueueSortType,
  useBulkDeletePostsMutation,
  useBulkUpdatePostsMutation,
  useGetCategoryProfilesQueueMetaLazyQuery,
  useGetPostFamilyLazyQuery,
  useGetPostIterationsLazyQuery,
  useShuffleQueueMutation,
  useUpdatePauseSettingsMutation,
  useUpdatePostMutation,
  useUpdatePostOrderMutation
} from '../../../models/graphql';
import { getCache } from '../../../apollo/Cache';
import { AppContext } from '../../../providers/AppProvider';

export type PostScheduleApolloProps = {
  context: PostScheduleViewContext;
};

gql`
  fragment CoreCursorPagination on QueueItemsCursorPagination {
    cursor
    count
    items {
      ...PostItem
    }
  }

  fragment CoreDateRangePagination on QueueItemsDateRangePagination {
    count
    startDate
    endDate
    items {
      ...PostItem
    }
  }

  query GetCategoryProfilesQueueMeta($workspaceId: ID!, $statusFilter: CategoryArchiveFilter = ACTIVE) {
    workspace(workspaceId: $workspaceId) {
      id
      categories(statusFilter: $statusFilter) {
        ...CoreCategoryFields
      }
      socialProfiles {
        ...SocialProfileFields
      }
      queueMeta {
        ...QueueMetaFields
      }
    }
  }

  query GetPost($postId: ID!) {
    post(postId: $postId) {
      ...PostItem
    }
  }

  query GetPostScheduleWeek($workspaceId: ID!, $input: QueueInput!) {
    workspace(workspaceId: $workspaceId) {
      id
      queue(input: $input) {
        __typename
        dateRangePagination {
          ...CoreDateRangePagination
        }
      }
    }
  }

  query GetPostScheduleList($workspaceId: ID!, $input: QueueInput!) {
    workspace(workspaceId: $workspaceId) {
      id
      queue(input: $input) {
        __typename
        cursorPagination {
          ...CoreCursorPagination
        }
      }
    }
  }

  query GetPostListAscending($workspaceId: ID!, $input: QueueInput!) {
    workspace(workspaceId: $workspaceId) {
      id
      queue(input: $input) {
        __typename
        cursorPagination {
          ...CoreCursorPagination
        }
      }
    }
  }

  query GetPostListDescending($workspaceId: ID!, $input: QueueInput!) {
    workspace(workspaceId: $workspaceId) {
      id
      queue(input: $input) {
        __typename
        cursorPagination {
          ...CoreCursorPagination
        }
      }
    }
  }

  query GetPostQueueCount($workspaceId: ID!, $queueFilters: QueueFilterInput!, $queueContext: QueueContext!) {
    workspace(workspaceId: $workspaceId) {
      id
      queueCounts(filterInput: $queueFilters, queueContext: $queueContext) {
        ...QueueCountsFields
      }
    }
  }

  query GetPostFamily($postId: ID!) {
    latestFamilyMembersOfPost(postId: $postId) {
      ...PostItem
    }
  }

  query GetPostIterations($postId: ID!) {
    postIterations(postId: $postId) {
      ...PostItem
    }
  }

  mutation ShuffleQueue($workspaceId: ID!, $filters: QueueFilterInput!) {
    shuffleQueue(workspaceId: $workspaceId, filters: $filters)
  }

  mutation UpdatePostOrder($postId: ID!, $direction: PostOrderUpdateDirection!, $relativePostId: ID!) {
    updatePostOrder(postId: $postId, direction: $direction, relativePostId: $relativePostId) {
      ...PostItem
    }
  }

  mutation UpdatePost($postId: ID!, $input: UpdatePostInput!) {
    updatePost(postId: $postId, input: $input) {
      ...PostItem
    }
  }

  mutation BulkUpdatePosts($input: [BulkUpdatePostInput!]!) {
    bulkUpdatePosts(input: $input) {
      ...PostItem
    }
  }

  mutation BulkDeletePosts($postIds: [ID!]!) {
    bulkDeletePosts(postIds: $postIds)
  }
`;

export const defaultQueueInput: QueueInput = {
  filters: {},
  context: QueueContext.SCHEDULE,
  pagination: {
    cursorPagination: {
      sortType: QueueSortType.CALCULATED_POST_TIME,
      sortOrder: QueueSortOrder.ASCENDING
    }
  }
};

export const clearPostCache = (workspaceId: string) => {
  const cache = getCache();

  // Modify the queue field in Workspace to remove all references to Post entities
  cache.modify({
    id: cache.identify({ id: workspaceId, __typename: 'Workspace' }),
    fields: {
      queue() {
        return [];
      }
    }
  });

  // Run garbage collection to remove now-orphaned entities
  cache.gc();
};

export const PostScheduleApollo = (props: PostScheduleApolloProps): JSX.Element => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const [loadingMore] = useState<boolean>(false);
  const [viewContext, setViewContext] = useState<PostScheduleViewContext>(props.context);

  const [getCategoryProfilesQueueMeta, categoryProfilesQueueMetaResults] = useGetCategoryProfilesQueueMetaLazyQuery({
    variables: { workspaceId: selectedWorkspaceId }
  });
  const [getGetPostFamily, postFamilyResults] = useGetPostFamilyLazyQuery();
  const [getPostIterations, postIterationsResults] = useGetPostIterationsLazyQuery();
  const [updatePost] = useUpdatePostMutation();
  const [updatePostOrder] = useUpdatePostOrderMutation();
  const [shuffleQueue] = useShuffleQueueMutation();
  const [bulkUpdatePosts] = useBulkUpdatePostsMutation();
  const [bulkDeletePosts] = useBulkDeletePostsMutation();
  const [updateWorkspacePauseSettings] = useUpdatePauseSettingsMutation();

  useEffect(() => {
    if (selectedWorkspaceId) {
      clearPostCache(selectedWorkspaceId);
    }
  }, [selectedWorkspaceId, viewContext]);

  const { context } = props;
  useEffect(() => {
    setViewContext(() => context);
  }, [context]);

  const controllerProps: PostScheduleControllerProps = {
    loadingMore,
    categories: categoryProfilesQueueMetaResults.data?.workspace.categories || [],
    socialProfiles: categoryProfilesQueueMetaResults.data?.workspace.socialProfiles || [],
    queueMeta: categoryProfilesQueueMetaResults.data?.workspace.queueMeta || [],
    context: viewContext,
    setContext: setViewContext,
    getCategoryProfilesQueueMeta,
    getGetPostFamily,
    postFamilyResults,
    getPostIterations,
    postIterationsResults,
    categoryProfilesQueueMetaResults,
    bulkDeletePosts,
    updatePost,
    updatePostOrder,
    bulkUpdatePosts,
    updateWorkspacePauseSettings,
    shuffleQueue
  };

  return <PostScheduleController {...controllerProps} />;
};
